import find from "lodash/find";
import {
  getKPIFormattedDeviation,
  getKPIFormattedRange,
  getKPIFormattedValue
} from "PFApp/helpers/kpi_formatter";
import { Card } from "PFComponents/card";
import { Inline, Stack } from "PFComponents/containers";
import { Tile } from "PFComponents/tile";
import { Typography } from "PFComponents/typography";
import { useKPIs } from "PFCore/hooks/queries/kpi";
import { KPI, KPIResult, KPIThresholdType } from "PFCore/types/kpi";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { KPIPill } from "../kpi_pill/kpi_pill";
import css from "./kpi_card.module.scss";

type KPICardProps = {
  className?: string;
  kpiResult: KPIResult;
} & (
  | { isReadOnly?: true; onClick?: never }
  | { onClick: (kpi: KPIResult) => void; isReadOnly?: false | never }
);

export const KPICard = ({ className, kpiResult, isReadOnly, onClick }: KPICardProps) => {
  const { t } = useTranslation("activities", { keyPrefix: "show.kpi" });
  const { data: kpi } = useKPIs<KPI | undefined>({
    select: (kpis) => find(kpis?.entries || [], ["id", kpiResult.indicatorId])
  });

  const Container = isReadOnly ? Tile : Card;

  const [roundedKpiValue, fullKpiValue] = useMemo(() => {
    if (!kpi) {
      return ["-", "-"];
    }

    const roundedValue = getKPIFormattedValue(kpiResult.value, kpi.unit);
    const fullValue = getKPIFormattedValue(kpiResult.value, kpi.unit, 3);
    return [roundedValue, fullValue];
  }, [kpiResult.value, kpi]);

  if (!kpi) {
    return null;
  }

  const rangeString = getKPIFormattedRange(
    kpiResult.threshold?.minValue ?? null,
    kpiResult.threshold?.maxValue ?? null,
    kpi.unit
  );
  const deviationString = getKPIFormattedDeviation(kpiResult.threshold?.deviationPercentage ?? null);
  const isDeviationThreshold = kpiResult.threshold?.thresholdType === KPIThresholdType.Deviation;
  const showThresholdData = isDeviationThreshold || !!rangeString;

  return (
    <Container
      className={className}
      paddingVariant="lg"
      onClick={onClick ? () => onClick(kpiResult) : undefined}
    >
      <Stack gap="spacingMd">
        <Typography variant="bodyBold" clipOverflow title={kpi.name}>
          {kpi.name}
        </Typography>
        <Inline alignItems="center">
          <Typography variant="h4" title={fullKpiValue}>
            {roundedKpiValue}
          </Typography>
          {showThresholdData && (
            <Typography variant="labelRegular" className={css.rangeText}>
              {isDeviationThreshold
                ? t("deviation", {
                    deviation: deviationString
                  })
                : t("range", {
                    range: rangeString
                  })}
            </Typography>
          )}
        </Inline>
        {kpiResult.status && <KPIPill kpiStatus={kpiResult.status} />}
      </Stack>
    </Container>
  );
};
